import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import { warningSVG3 } from 'app/assets/svgs';
import { updateCurrentLoanAmount, updateLoan } from 'app/domain/loan/slice';
import selectLoanState from 'app/domain/loan/slice/selectors';
import selectProfileState from 'app/domain/profile/slice/selectors';
import withLayout from 'app/hoc/withLayout';
import usePostCalculateInstallment from 'app/pages/LoanEstimationPage/hooks/usePostCalculateInstallment';
import useGetApplicationDetail from 'app/pages/VehicleAndLoanReview/hooks/useGetApplicationDetail';
import { DocumentTitle, RoutePath } from 'app/Routing/constants';
import { Title } from 'app/SharedComponents';
import ListNoPaging from 'app/SharedComponents/ListNoPaging';
import Loader from 'app/SharedComponents/Loader';
import { ModalConfirmation } from 'app/SharedComponents/Modal';
import { OptionModel } from 'app/SharedComponents/Option/interfaces';
import { transformToIDR } from 'utils/transformer';

const InstallmentListPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const { form, loan_amount } = useSelector(selectLoanState);
  const { profileData } = useSelector(selectProfileState);
  const { applicationId } = useSelector(selectProfileState);
  const { data: applicationData } = useGetApplicationDetail(
    applicationId || '',
  );
  const [installmentList, setInstallmentList] = useState<any>();
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [selectedLoanTenure, setSelectedLoanTenure] = useState<any>();
  const {
    mutate: postCalculateInstallment,
    isLoading: isPostCalculateInstallmentLoading,
  } = usePostCalculateInstallment();

  const onSubmit = loanInfo => {
    const { tenor, ...restLoanInfo } = loanInfo;
    if (loanInfo.ntf_amount !== loan_amount) {
      setSelectedLoanTenure(loanInfo);
      setIsModalOpen(true);
    } else {
      dispatch(
        updateLoan({
          ...form.loan,
          ...restLoanInfo,
          tenure: loanInfo.tenor,
          proposed_loan_amount: loanInfo.ntf_amount,
          monthly_installment: loanInfo.rounded_up_installment,
          asset_insurance_id: loanInfo.asset_insurance_calculation_id,
          life_insurance_id: loanInfo.life_insurance_calculation_id,
          total_admin_fee: loanInfo.admin_fee,
        }),
      );
      navigate(RoutePath.loanEstimation, { state: location.pathname });
    }
  };

  const optionModelTenor = (tenor): OptionModel => {
    return {
      value: tenor['tenor'],
      title: `${tenor['tenor']} ${t('month')}`,
      name: `Rp. ${transformToIDR(tenor['rounded_up_installment'])}/ ${t(
        'month',
      )}`,
    };
  };

  useEffect(() => {
    if (form) {
      const { customer_type } = form.loan;
      const { package_id, product_id, city } = form;
      const {
        address: { sub_district_code },
      } = profileData;

      const calculationPayload = sub_district_code
        ? { sub_district_code }
        : { city_code: city.code };

      const payload = {
        calculation: {
          ...calculationPayload,
          customer_type,
          package_id,
          product_id,
          loan_amount,
        },
        collateral: {
          asset_code: form.variant.variant_code,
          manufacturing_year: form.year,
          category_id: form.variant.category_id,
          is_new: form.variant.is_new,
          asset_type_id: form.variant.asset_type_id,
          otr: form.collateral.asset_price,
        },
      };
      postCalculateInstallment(payload, {
        onSuccess: res => {
          setInstallmentList(res.data.calculation);
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  if (isPostCalculateInstallmentLoading) {
    return <Loader />;
  }
  const confirmModalHandler = () => {
    const {
      tenor,
      ntf_amount,
      rounded_up_installment,
      asset_insurance_calculation_id,
      life_insurance_calculation_id,
      admin_fee,
      ...restLoanInfo
    } = selectedLoanTenure;
    dispatch(
      updateLoan({
        ...form.loan,
        ...restLoanInfo,
        tenure: tenor,
        proposed_loan_amount: ntf_amount,
        monthly_installment: rounded_up_installment,
        asset_insurance_id: asset_insurance_calculation_id,
        life_insurance_id: life_insurance_calculation_id,
        total_admin_fee: admin_fee,
      }),
    );
    dispatch(updateCurrentLoanAmount(ntf_amount));

    navigate(RoutePath.loanEstimation, { state: location.pathname });
  };
  const closeModalHandler = () => {
    navigate(RoutePath.loanEstimation, { state: location.pathname });
  };
  const cancelModalHandler = () => {
    setIsModalOpen(false);
  };
  return (
    <>
      <Helmet>
        <title>{DocumentTitle.loanEstimation}</title>
      </Helmet>
      <Title level={4} className="font-medium">
        {t('loanEstimationTenure')}
      </Title>
      <p className="mb-8 text-typo-normal text-sm mt-2 dark:text-grayscale-lighter">
        {t('loanEstimationTenureDescription')}
      </p>
      {installmentList && (
        <ListNoPaging
          onSubmit={onSubmit}
          parser={optionModelTenor}
          data={installmentList}
          className="h-[49vh]"
          selectedItem={{
            tenor: form.loan.tenure,
            rounded_up_installment: form.loan.monthly_installment,
          }}
          data-testid="InstallmentList-component"
          checkedOnChange={false}
        />
      )}
      <ModalConfirmation
        isOpen={isModalOpen}
        buttonCancelText={t('changeLoanMessage')}
        buttonConfirmText={t('agreeLoanMessage')}
        onCancel={cancelModalHandler}
        onConfirm={confirmModalHandler}
        onClickCloseIcon={closeModalHandler}
        buttonAlignment="column"
        isCancelIcon
      >
        <div className="flex flex-col items-center justify-center text-center">
          <img
            className="w-10 h-10 mb-4"
            src={warningSVG3}
            alt="Warning Icon"
          />
          <Title
            level={4}
            className="font-semibold text-typo-title dark:text-grayscale-lighter text-base"
          >
            {t('loanAdjustTitle')}
          </Title>
          <p
            className="mt-4 text-typo-normal dark:text-grayscale-lighter text-sm"
            dangerouslySetInnerHTML={{
              __html: t('loanAdjustDescription', {
                name: applicationData?.verification_identity.full_name,
              }),
            }}
          />
          <span className="font-medium text-2xl text-primary m-5">
            Rp. {transformToIDR(selectedLoanTenure?.ntf_amount)}
          </span>
        </div>
      </ModalConfirmation>
    </>
  );
};

export default withLayout(InstallmentListPage, 'back');
